import { clsx } from "clsx";
import { FC, PropsWithChildren, Suspense } from "react";
import { Helmet } from "react-helmet";
import { graphql, useFragment } from "react-relay";
import { Routes, useLocation } from "react-router-dom";
import { Link, route } from "../Gate";
import { useAccounts } from "../services/accounts";
import { Balances } from "./components/Balances";
//import { Merge } from "./components/Merge";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Fiat, i18n, nFormatter, Toggle, useLocalStorage } from "rujira.ui";
import { External } from "rujira.ui/src/components/icons/Icons";
import { usePreloadedAccountData } from "../services/accountData";
import { AccountSelect } from "./components/AccountSelect";
import { Bonded } from "./components/Bonded";
//import { Buy, Deposit } from "./components/Cta";
import { createContext, useContext } from "react";
import { usePreloadedBalanceData } from "../common/components/Balance";
import {
  PortfolioFragment$data,
  PortfolioFragment$key,
} from "./__generated__/PortfolioFragment.graphql";
import { Connect } from "./components/Connect";
import { Merging } from "./components/Merging";
import { Orders } from "./components/Orders";
import { Positions } from "./components/Positions";
import { Strategies } from "./components/Strategies";
import { Streaming } from "./components/Streaming";
import { balancesValue, totalValue } from "./utils";

export const PortfolioFragment = graphql`
  fragment PortfolioFragment on Account {
    ...MergePortfolioAccountFragment
    fin {
      ordersV2(first: 100) {
        edges {
          cursor
          node {
            __typename
            ... on FinOrder {
              valueUsd
            }
            ... on CalcOrder {
              valueUsd
              balances {
                amount
              }
              ...RecurringOrdersFragment
            }
          }
        }
      }
    }

    merge {
      accounts {
        merged {
          amount
        }
        valueUsd
      }
    }

    staking {
      single {
        bonded {
          amount
        }
        liquid {
          amount
        }
      }
    }
    credit {
      accounts {
        id
        valueUsd
      }
    }
    strategies {
      __typename
      ... on ThorchainLiquidityProvider {
        id
        valueUsd
      }
      ... on BowAccount {
        id
        valueUsd
      }
      ... on StakingAccount {
        id
        valueUsd
      }
      ... on PerpsAccount {
        id
        valueUsd
      }
      ... on IndexAccount {
        id
        sharesValue
      }
      ... on GhostVaultAccount {
        id
        valueUsd
      }
    }
  }
`;

enum Tab {
  Balances = "",
  Merging = "/merging",
  Streaming = "/streaming",
  Bonded = "/bonded",
  Strategies = "/strategies",
  Lent = "/lent",
  Borrowing = "/borrowing",
  Orders = "/orders",
}

export const Portfolio: FC = () => {
  const { selected } = useAccounts();

  return (
    <>
      <Meta />

      {selected ? (
        <div className="rujira__main rujira__main--gradient">
          <Container>
            <MyAccount />
          </Container>
        </div>
      ) : (
        <Connect />
      )}
    </>
  );
};

const Meta = () => {
  return (
    <Helmet>
      <title>Rujira Portfolio</title>
      <meta name="description" content="View your account portfolio." />
      <meta name="og:description" content="View your account portfolio." />
      <meta name="twitter:description" content="View your account portfolio." />
    </Helmet>
  );
};

const Container: FC<PropsWithChildren> = ({ children }) => {
  return (
    <div className="rujira__inner--pad portfolio">
      <div className="rujira__inner">
        <div className="row wrap pad ai-c">
          <div className="col-12 col-lg-5">
            <h1 className="h2">My Portfolio</h1>
            <AccountSelect />
          </div>
          <PortfolioProvider>
            <Suspense fallback={<Fallback />}>{children}</Suspense>
          </PortfolioProvider>
        </div>
      </div>
    </div>
  );
};

const MyAccount: FC = () => {
  const { accountData } = usePreloadedAccountData();
  const account = useFragment<PortfolioFragment$key>(
    PortfolioFragment,
    accountData
  );
  const balances = usePreloadedBalanceData();

  const value =
    account && balances?.ok ? totalValue(account, balances.value.edges) : 0n;

  const tabs = [
    {
      tab: Tab.Balances,
      label: "Balances",
      value: balances?.ok ? balancesValue(balances.value.edges) : 0n,
    },
    ...(account ? accountTabs(account) : []),
  ];

  return <Content value={value} tabs={tabs} />;
};

const accountTabs = (account: PortfolioFragment$data) => [
  {
    tab: Tab.Merging,
    label: "Merging",
    count: account?.merge?.accounts?.filter((x) => x.merged.amount !== 0n)
      .length,
    hideable: true,
    value: (account?.merge?.accounts || []).reduce(
      (a, v) => a + BigInt(v?.valueUsd || 0),
      0n
    ),
  },
  // { tab: Tab.Streaming, label: "Streaming Payments", count: 1 },
  {
    tab: Tab.Orders,
    label: "Orders",
    count: account?.fin?.ordersV2?.edges?.filter(
      (o) =>
        !!o?.node &&
        (o.node.__typename === "FinOrder" ||
          (o.node.__typename === "CalcOrder" &&
            o.node.balances?.some((b) => Number(b?.amount) > 10)))
    ).length,
    hideable: true,
    value: (account?.fin?.ordersV2?.edges || []).reduce(
      (a, v) =>
        a +
        BigInt(
          !!v?.node && "valueUsd" in v?.node && v?.node.valueUsd
            ? v?.node?.valueUsd
            : 0
        ),
      0n
    ),
  },
  {
    tab: Tab.Borrowing,
    label: "Borrowing",
    count: account?.credit.accounts?.length,
    hideable: true,
    value: (account?.credit.accounts || []).reduce(
      (a, v) => a + BigInt(v.valueUsd),
      0n
    ),
  },
  {
    tab: Tab.Strategies,
    label: "Strategies",
    count: account?.strategies.length,
    hideable: true,
    value: (account?.strategies || []).reduce((a, v) => {
      return (
        a +
        BigInt(
          v.__typename === "%other"
            ? 0
            : v.__typename === "IndexAccount"
              ? v.sharesValue
              : v?.valueUsd || 0
        )
      );
    }, 0n),
  },
];

const Content: FC<{
  value: bigint;
  tabs: {
    tab: Tab;
    label: string;
    count?: number;
    hideable?: boolean;
    value?: bigint;
  }[];
}> = ({ value, tabs }) => {
  const chart = tabs.map((x) => ({
    name: x.label,
    value: Number(x.value) / 10 ** 8,
  }));

  const colors = ["#D615EB", "#7e0c8a", "#8436F5", "#5A2AD1", "#60fbd0"];

  return (
    <>
      <div className="col-12 col-lg-7 mt-2">
        <div className="card h-full p-3 flex wrap gap-1">
          <div className="flex dir-c grow">
            <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">
              Wallet Value
            </i18n.h3>
            <Fiat
              symbol="$"
              amount={value}
              decimals={8}
              className="fs-42 condensed fw-500 mt-1"
            />
          </div>
          {value > 0n && (
            <div className="w-10 h-10">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart width={80} height={80}>
                  <Pie
                    dataKey="value"
                    data={chart}
                    cx="50%"
                    cy="50%"
                    innerRadius={20}
                    outerRadius={40}
                    strokeWidth={0}
                    fill="#82ca9d">
                    {chart.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={colors[index]} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={<CustomTooltip />}
                    allowEscapeViewBox={{ x: true, y: true }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="col-12">
        <Tabs tabs={tabs} />
      </div>
      <div className="col-12">
        <TabContent />
      </div>
    </>
  );
};

const Fallback: FC = () => (
  <>
    <div className="col-12 col-lg-7 mt-2">
      <div className="card h-16 p-3">
        <i18n.h3 className="fs-16 lh-22 fw-400 color-grey">
          Wallet Value
        </i18n.h3>
        <div className="skeleton h-6 w-24 br-10 mt-1.5 mb-2" />
      </div>
    </div>

    <div className="col-12">
      <div className="tabs mt-4">
        <a className="skeleton w-12" />
      </div>
    </div>
    <div className="col-12">
      <div className="relative card p-3 mt-2">
        <table className="table table--big condensed portfolio__balances table--no-hover">
          <tbody>
            {[...Array(3)].map((_, i) => (
              <tr key={`skeleton-${i}`}>
                <td className="w-1">
                  <div className="skeleton h-4 w-4 br-4" />
                </td>
                <td>
                  <div className="skeleton h-2 w-12 br-2" />
                </td>
                <td className="text-right">
                  <div className="skeleton h-2 w-10 br-2 inline-block" />
                </td>
                <td className="text-right">
                  <div className="skeleton h-2 w-10 br-2 inline-block" />
                </td>
                <td className="text-right">
                  <div className="skeleton h-2 w-6 br-2 inline-block" />
                </td>
                <td className="text-right">
                  <div className="skeleton h-2 w-10 br-2 inline-block" />
                </td>
                <td className="w-1">
                  <div className="skeleton h-4 w-4 br-0.5" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </>
);
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="card bg-darkGrey p-1.5 fs-14 condensed">
        <p className="label">
          {payload[0].name}
          <br />${payload[0].value.toLocaleDecimal(2)}
        </p>
      </div>
    );
  }
  return null;
};

const Tabs: FC<{
  tabs: {
    tab: Tab;
    label: string;
    count?: number;
    hideable?: boolean;
    value?: bigint;
  }[];
}> = ({ tabs }) => {
  const { selected } = useAccounts();
  const { hideSmall, setHideSmall } = usePortfolio();
  const loc = useLocation();

  return (
    <div className="tabs mt-4">
      {tabs.map((x) =>
        x.hideable === true && x.count === 0 ? null : (
          <TabItem key={x.label} tab={x.tab}>
            <div className="flex row ai-c">
              {x.label}
              {x.value !== undefined && x.value > 0n ? (
                <div className="tag tag--teal tag--md ml-1">
                  ${nFormatter(x.value || 0n, 2)}
                </div>
              ) : null}
              {x.count ? <i>{x.count}</i> : null}
            </div>
          </TabItem>
        )
      )}

      <a
        className="ml-a mr-0"
        href={`https://thorchain.net/address/${selected?.address}`}
        target="_blank">
        History
        <External className="ml-0.5" />
      </a>
      {loc.pathname.replace("/portfolio", "") === Tab.Balances && (
        <Toggle
          label="Hide low value balances"
          className="toggle--xs color-white as-c ml-1"
          checked={hideSmall}
          onChange={(e) => setHideSmall(e.currentTarget.checked)}
        />
      )}
    </div>
  );
};

const TabItem: FC<
  PropsWithChildren<{
    tab: Tab;
    counter?: number;
  }>
> = ({ tab, counter, children }) => {
  const loc = useLocation();
  const isSelected = loc.pathname.replace("/portfolio", "") === tab;

  return (
    <Link
      to={`/portfolio${tab}`}
      className={clsx({ "selected bg-black": isSelected })}>
      {children} {counter ? <i>{counter}</i> : null}
    </Link>
  );
};

const TabContent: FC = () => (
  <Routes>
    {route({
      path: "",
      element: <Balances />,
    })}
    {route({
      path: Tab.Streaming,
      element: <Streaming />,
    })}
    {route({
      path: Tab.Merging,
      element: <Merging />,
    })}

    {route({
      path: Tab.Orders,
      element: <Orders />,
    })}

    {route({
      path: Tab.Strategies,
      element: <Strategies />,
    })}

    {route({
      path: Tab.Bonded,
      element: <Bonded />,
    })}

    {route({
      path: Tab.Borrowing,
      element: <Positions />,
    })}

    {route({
      path: "*",
      element: (
        <div className="card text-center p-3 mt-2 condensed color-grey">
          Coming soon...
        </div>
      ),
    })}
  </Routes>
);

interface PortfolioContextType {
  hideSmall: boolean;
  setHideSmall: (value: boolean) => void;
}

const PortfolioContext = createContext<PortfolioContextType | undefined>(
  undefined
);

export const PortfolioProvider: FC<PropsWithChildren> = ({ children }) => {
  const [hideSmall, setHideSmall] = useLocalStorage(
    "rujira-portfolio-small",
    false
  );

  return (
    <PortfolioContext.Provider value={{ hideSmall, setHideSmall }}>
      {children}
    </PortfolioContext.Provider>
  );
};

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (context === undefined) {
    throw new Error("usePortfolio must be used within a PortfolioProvider");
  }
  return context;
};
